import React from 'react';
import ResponsiveECharts from './ResponsiveECharts';
import { baseOption, axeCategorie, axeValeur } from './echartsTheme';

// Une plage de couleurs par famille d'animaux : les barres d'une même famille
// se dégradent du foncé au clair, ce qui permet de distinguer les ateliers d'un
// coup d'œil sans légende.
const PLAGES = {
  bovin: { debut: '#6C2B72', fin: '#C98AD0' },
  // Cochons : #FF9EF7, avec une version plus claire pour la fin du dégradé.
  porcin: { debut: '#FF9EF7', fin: '#FFCEFB' },
};

/**
 * Famille d'un animal d'après son libellé, même logique que
 * demandeAbreuvement() : un ajout de catégorie porcine fonctionne sans
 * toucher à cette fonction.
 */
function famille(libelle = '') {
  return /porc|truie/i.test(libelle) ? 'porcin' : 'bovin';
}

function versRvb(hex) {
  return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
}

/**
 * Couleur intermédiaire entre deux bornes.
 * `position` va de 0 (borne de début) à 1 (borne de fin).
 */
function interpoler(debut, fin, position) {
  const a = versRvb(debut);
  const b = versRvb(fin);
  const composantes = a.map((valeur, i) =>
    Math.round(valeur + (b[i] - valeur) * position)
  );
  return `#${composantes.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Effectifs du cheptel par catégorie d'animaux (bloc "Elevage" du Tableau de
 * bord, image 492 de la maquette).
 *
 * @param data [{ categorie: string, effectif: number }]
 */
export default function ElevageChart({ data = [] }) {
  // Une catégorie à 0 (ou sans valeur) n'est pas affichée : elle occuperait une
  // graduation de l'axe sans rien montrer.
  const donnees = data.filter((d) => Number(d.effectif) > 0);

  // Aucune catégorie non nulle : l'exploitation n'a pas d'atelier d'élevage.
  if (donnees.length === 0) {
    return <p className="tdb-chargement">Pas d'élevage dans l'exploitation</p>;
  }

  // Le dégradé est réparti sur les barres réellement affichées : si une
  // catégorie est masquée parce qu'elle vaut 0, les couleurs restantes se
  // répartissent quand même sur toute la plage.
  const effectifsParFamille = donnees.reduce((compte, d) => {
    const f = famille(d.categorie);
    compte[f] = (compte[f] ?? 0) + 1;
    return compte;
  }, {});
  const rangs = {};

  const barres = donnees.map((d) => {
    const f = famille(d.categorie);
    const total = effectifsParFamille[f];
    const rang = rangs[f] ?? 0;
    rangs[f] = rang + 1;

    const plage = PLAGES[f];
    // Une seule barre dans la famille : on garde la teinte la plus foncée.
    const position = total > 1 ? rang / (total - 1) : 0;

    return {
      value: d.effectif,
      itemStyle: { color: interpoler(plage.debut, plage.fin, position) },
    };
  });

  const option = {
    ...baseOption(),
    // Une seule série : pas de légende à afficher.
    legend: { show: false },
    grid: { left: 12, right: 24, bottom: 56, top: 56, containLabel: true },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      textStyle: { fontSize: 14, color: '#000000' },
      valueFormatter: (valeur) => `${valeur} têtes`,
    },
    xAxis: axeCategorie({ data: donnees.map((d) => d.categorie) }),
    yAxis: axeValeur({ nom: 'Nombre de têtes' }),
    series: [
      {
        type: 'bar',
        barMaxWidth: 64,
        data: barres,
      },
    ],
  };

  return <ResponsiveECharts option={option} style={{ height: 340 }} />;
}
